import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Loader2, Search, Package, Truck, CheckCircle2, XCircle, MoreHorizontal, MapPin, ExternalLink, Banknote, ArrowLeftRight, Image as ImageIcon, Eye, Check, X, ClipboardCheck, Phone, StickyNote, MessageCircle } from 'lucide-react';
import UserProfileDialog from '../../components/UserProfileDialog';
import { UserAvatar } from '../../components/VapeAvatars';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';

const STATUS_META = {
  'Preparando': {
    icon: Package,
    ring: 'ring-orange-500/30',
    dot: 'bg-orange-400',
    text: 'text-orange-300',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
  },
  'Pedido confirmado': {
    icon: ClipboardCheck,
    ring: 'ring-green-500/30',
    dot: 'bg-green-400',
    text: 'text-green-300',
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
  },
  'En camino': {
    icon: Truck,
    ring: 'ring-sky-500/30',
    dot: 'bg-sky-400',
    text: 'text-sky-300',
    bg: 'bg-sky-500/10',
    border: 'border-sky-500/30',
  },
  'Entregado': {
    icon: CheckCircle2,
    ring: 'ring-emerald-500/30',
    dot: 'bg-emerald-400',
    text: 'text-emerald-300',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
  },
  'Cancelado': {
    icon: XCircle,
    ring: 'ring-red-500/30',
    dot: 'bg-red-400',
    text: 'text-red-300',
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
  },
};
const STATUS = Object.keys(STATUS_META);

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META['Preparando'];
  const Ic = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full ${meta.bg} ${meta.border} border ${meta.text} px-2.5 py-1 text-[11px] font-bold tracking-wide uppercase`}>
      <Ic className="w-3 h-3" />
      {status}
    </span>
  );
};

const StatusSelect = ({ value, onChange }) => {
  const meta = STATUS_META[value] || STATUS_META['Preparando'];
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className={`h-9 w-[150px] rounded-full ${meta.bg} ${meta.border} border ${meta.text} font-bold text-xs uppercase tracking-wide focus:ring-2 focus:${meta.ring} hover:brightness-110 transition`}
        data-testid={`admin-order-status-trigger`}
      >
        <span className={`inline-flex items-center gap-1.5`}>
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot} animate-pulse`} />
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent className="bg-neutral-950 border-white/10 text-white">
        {STATUS.map((s) => {
          const m = STATUS_META[s];
          const Ic = m.icon;
          return (
            <SelectItem
              key={s}
              value={s}
              className={`focus:bg-white/[0.06] focus:${m.text} cursor-pointer`}
              data-testid={`admin-order-status-option-${s}`}
            >
              <span className="inline-flex items-center gap-2">
                <Ic className={`w-3.5 h-3.5 ${m.text}`} />
                <span className="font-semibold">{s}</span>
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

const OrdersSection = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [profileUser, setProfileUser] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [proofOrder, setProofOrder] = useState(null);
  const [cancelDialog, setCancelDialog] = useState(null); // { order_id }
  const [cancelReason, setCancelReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/orders').then((r) => setOrders(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const change = async (order_id, status) => {
    if (status === 'Cancelado') {
      setCancelReason('');
      setCancelDialog({ order_id });
      return;
    }
    setBusyId(order_id);
    try {
      await api.patch(`/admin/orders/${order_id}/status`, { status });
      setOrders((prev) => prev.map((o) => (o.order_id === order_id ? { ...o, status } : o)));
      toast({ title: 'Pedido actualizado', description: `${order_id} → ${status}` });
    } catch (err) {
      toast({ title: 'No se pudo actualizar', description: err?.response?.data?.detail || 'Intenta de nuevo.' });
    } finally {
      setBusyId(null);
    }
  };

  const confirmCancel = async () => {
    if (!cancelDialog) return;
    const reason = cancelReason.trim();
    if (reason.length < 5) {
      toast({ title: 'Motivo demasiado corto', description: 'Describe el motivo con al menos 5 caracteres.' });
      return;
    }
    setCancelBusy(true);
    try {
      await api.patch(`/admin/orders/${cancelDialog.order_id}/status`, { status: 'Cancelado', reason });
      setOrders((prev) => prev.map((o) => (o.order_id === cancelDialog.order_id ? { ...o, status: 'Cancelado', cancel_reason: reason } : o)));
      toast({ title: 'Pedido cancelado', description: 'Se envió correo al cliente con el motivo.' });
      setCancelDialog(null);
      setCancelReason('');
    } catch (err) {
      toast({ title: 'No se pudo cancelar', description: err?.response?.data?.detail || 'Intenta de nuevo.' });
    } finally {
      setCancelBusy(false);
    }
  };

  const reviewPayment = async (order_id, payment_status) => {
    try {
      await api.patch(`/admin/orders/${order_id}/payment`, { payment_status });
      setOrders((prev) => prev.map((o) => (o.order_id === order_id ? { ...o, payment_status } : o)));
      setProofOrder((cur) => (cur?.order_id === order_id ? { ...cur, payment_status } : cur));
      toast({ title: payment_status === 'approved' ? 'Pago aprobado' : 'Pago rechazado' });
    } catch (err) {
      toast({ title: 'No se pudo actualizar', description: err?.response?.data?.detail || 'Intenta de nuevo.' });
    }
  };

  const counts = useMemo(() => {
    const c = { all: orders.length };
    STATUS.forEach((s) => { c[s] = orders.filter((o) => o.status === s).length; });
    return c;
  }, [orders]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      if (filter !== 'all' && o.status !== filter) return false;
      if (!q) return true;
      return (
        (o.order_id || '').toLowerCase().includes(q) ||
        (o.customer_name || '').toLowerCase().includes(q) ||
        (o.customer_phone || '').toLowerCase().includes(q) ||
        (o.customer_email || '').toLowerCase().includes(q) ||
        (o.address || '').toLowerCase().includes(q)
      );
    });
  }, [orders, filter, query]);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">Pedidos</h1>
          <p className="text-white/50 text-sm mt-1">
            {orders.length} pedidos en total · Haz clic en el cliente para ver su perfil completo.
          </p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por ID, cliente, teléfono, dirección…"
            className="pl-9 h-10 bg-neutral-950 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50"
            data-testid="admin-orders-search"
          />
        </div>
      </div>

      {/* Filter tabs */}
      <div className="mt-5 flex flex-wrap gap-2">
        {['all', ...STATUS].map((s) => {
          const active = filter === s;
          const meta = s === 'all' ? null : STATUS_META[s];
          return (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`inline-flex items-center gap-2 h-9 px-4 rounded-full text-sm font-semibold border transition-all ${
                active
                  ? 'bg-orange-500 text-black border-orange-500 shadow-md shadow-orange-500/25'
                  : 'bg-neutral-950 border-white/10 text-white/70 hover:text-white hover:border-white/20'
              }`}
              data-testid={`admin-orders-filter-${s}`}
            >
              {meta && <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-black/70' : meta.dot}`} />}
              {s === 'all' ? 'Todos' : s}
              <span className={`ml-0.5 text-xs font-bold rounded-full px-1.5 py-0.5 ${active ? 'bg-black/15 text-black' : 'bg-white/5 text-white/50'}`}>
                {counts[s] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="mt-8 flex items-center gap-2 text-white/60">
          <Loader2 className="w-5 h-5 animate-spin text-orange-500" /> Cargando pedidos…
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-white/10 bg-gradient-to-b from-neutral-950 to-black overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-white/[0.03] text-white/50 text-[11px] uppercase tracking-[0.12em]">
                <tr>
                  <th className="text-left font-bold px-4 py-3">Pedido</th>
                  <th className="text-left font-bold px-4 py-3">Cliente</th>
                  <th className="text-left font-bold px-4 py-3">Fecha</th>
                  <th className="text-left font-bold px-4 py-3">Dirección</th>
                  <th className="text-right font-bold px-4 py-3">Total</th>
                  <th className="text-left font-bold px-4 py-3">Pago</th>
                  <th className="text-left font-bold px-4 py-3">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {visible.map((o, i) => {
                  const meta = STATUS_META[o.status] || STATUS_META['Preparando'];
                  const isBusy = busyId === o.order_id;
                  return (
                    <tr
                      key={o.order_id}
                      className={`group transition-colors ${i % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.015]'} hover:bg-white/[0.04]`}
                      data-testid={`admin-order-row-${o.order_id}`}
                    >
                      <td className="px-4 py-3 align-middle">
                        <div className="flex items-center gap-2">
                          <span className={`w-1 h-8 rounded-full ${meta.dot} opacity-70 group-hover:opacity-100 transition`} />
                          <span className="text-white font-mono text-xs tracking-tight">{o.order_id}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <button
                          onClick={() => setProfileUser(o.user_id)}
                          className="flex items-center gap-2.5 rounded-lg px-1.5 py-1 -mx-1.5 hover:bg-white/[0.06] transition-colors max-w-[220px]"
                        >
                          <UserAvatar picture={o.customer_picture} name={o.customer_name} phone={o.customer_phone} email={o.customer_email} size={32} />
                          <div className="text-left min-w-0">
                            <p className="text-white font-semibold group-hover:text-orange-400 truncate text-sm">
                              {o.customer_name || o.customer_phone || o.customer_email || 'Sin nombre'}
                            </p>
                            <p className="text-white/45 text-[11px] truncate">
                              {o.customer_email || o.customer_phone || '—'}
                            </p>
                          </div>
                        </button>
                      </td>
                      <td className="px-4 py-3 align-middle text-white/70 text-xs whitespace-nowrap">
                        {new Date(o.created_at).toLocaleString('es-MX', {
                          day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                        })}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="max-w-[300px] space-y-1.5">
                          <div className="flex items-start gap-1.5 text-white/60 text-xs">
                            <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-white/30" />
                            <span className="line-clamp-2">{o.address || '—'}</span>
                          </div>
                          {o.contact_phone ? (
                            <div className="flex items-center gap-2 text-xs">
                              <Phone className="w-3.5 h-3.5 shrink-0 text-emerald-400" />
                              <span className="text-white font-mono font-bold">{o.contact_phone}</span>
                              <a
                                href={`https://wa.me/${(o.contact_phone || '').replace(/\D/g, '')}`}
                                target="_blank" rel="noreferrer"
                                className="ml-0.5 inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-400 hover:text-emerald-300"
                                data-testid={`admin-order-wa-${o.order_id}`}
                              >
                                <MessageCircle className="w-3 h-3" /> WA
                              </a>
                              <a
                                href={`tel:+${(o.contact_phone || '').replace(/\D/g, '')}`}
                                className="inline-flex items-center gap-0.5 text-[10px] font-bold text-orange-400 hover:text-orange-300"
                                data-testid={`admin-order-tel-${o.order_id}`}
                              >
                                <Phone className="w-3 h-3" /> Llamar
                              </a>
                            </div>
                          ) : null}
                          {o.notes ? (
                            <div className="flex items-start gap-1.5 text-[11px] text-white/50 leading-snug">
                              <StickyNote className="w-3 h-3 mt-0.5 shrink-0 text-orange-400" />
                              <span className="line-clamp-2 italic">"{o.notes}"</span>
                            </div>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle text-right whitespace-nowrap">
                        <span className="text-white font-black text-base">
                          ${o.total?.toLocaleString('es-MX')}
                        </span>
                        <span className="text-white/40 text-[10px] ml-0.5">MXN</span>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <PaymentCell order={o} onViewProof={() => setProofOrder(o)} />
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="flex items-center gap-2">
                          <StatusSelect value={o.status} onChange={(v) => change(o.order_id, v)} />
                          {isBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-400" />}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center">
                      <div className="inline-flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center">
                          <Package className="w-6 h-6 text-white/40" />
                        </div>
                        <p className="text-white/60 text-sm">Sin pedidos en esta vista.</p>
                        {(filter !== 'all' || query) && (
                          <button
                            onClick={() => { setFilter('all'); setQuery(''); }}
                            className="text-orange-400 text-xs font-bold hover:text-orange-300"
                          >
                            Limpiar filtros
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {visible.length > 0 && (
            <div className="px-4 py-3 border-t border-white/[0.06] flex items-center justify-between text-[11px] text-white/40">
              <span>{visible.length} de {orders.length} pedidos</span>
              <span className="inline-flex items-center gap-3">
                {STATUS.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_META[s].dot}`} /> {s}
                  </span>
                ))}
              </span>
            </div>
          )}
        </div>
      )}

      <UserProfileDialog userId={profileUser} open={!!profileUser} onOpenChange={(v) => !v && setProfileUser(null)} />

      {/* Payment proof review dialog */}
      <Dialog open={!!proofOrder} onOpenChange={(v) => !v && setProofOrder(null)}>
        <DialogContent className="sm:max-w-2xl bg-neutral-950 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ArrowLeftRight className="w-4 h-4 text-orange-400" />
              Comprobante de transferencia
            </DialogTitle>
          </DialogHeader>
          {proofOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="rounded-lg bg-white/[0.03] border border-white/10 p-3">
                  <p className="text-white/40 uppercase tracking-wider text-[10px] font-bold">Pedido</p>
                  <p className="text-white font-mono mt-0.5">{proofOrder.order_id}</p>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/10 p-3">
                  <p className="text-white/40 uppercase tracking-wider text-[10px] font-bold">Cliente</p>
                  <p className="text-white truncate mt-0.5">{proofOrder.customer_name || proofOrder.customer_email || '—'}</p>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/10 p-3">
                  <p className="text-white/40 uppercase tracking-wider text-[10px] font-bold">Total</p>
                  <p className="text-white font-black mt-0.5">${proofOrder.total?.toLocaleString('es-MX')} <span className="text-white/40 text-[10px]">MXN</span></p>
                </div>
              </div>

              {proofOrder.payment_proof ? (
                <div className="rounded-xl overflow-hidden border border-white/10 bg-black">
                  <img src={proofOrder.payment_proof} alt="Comprobante" className="w-full max-h-[60vh] object-contain" />
                </div>
              ) : (
                <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-6 text-center text-yellow-300">
                  El cliente no adjuntó comprobante todavía.
                </div>
              )}

              <div className="flex items-center justify-between gap-3 pt-2 border-t border-white/10">
                <div className="text-xs">
                  <span className="text-white/40 mr-1.5 uppercase tracking-wider font-bold text-[10px]">Estado del pago:</span>
                  <PaymentBadge status={proofOrder.payment_status} />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => reviewPayment(proofOrder.order_id, 'rejected')}
                    className="h-9 rounded-full border-red-500/25 text-red-300 hover:bg-red-500/10"
                    data-testid="payment-reject-btn"
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Rechazar
                  </Button>
                  <Button
                    onClick={() => reviewPayment(proofOrder.order_id, 'approved')}
                    className="h-9 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold"
                    data-testid="payment-approve-btn"
                  >
                    <Check className="w-3.5 h-3.5 mr-1" /> Aprobar pago
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel order dialog with reason */}
      <Dialog open={!!cancelDialog} onOpenChange={(v) => { if (!v) { setCancelDialog(null); setCancelReason(''); } }}>
        <DialogContent className="sm:max-w-lg bg-neutral-950 border border-white/10 text-white" data-testid="cancel-order-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-400" />
              Cancelar pedido {cancelDialog?.order_id}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-white/70 leading-relaxed">
              Describe con el mayor <strong className="text-white">detalle posible</strong> el motivo de la cancelación. Se enviará por correo al cliente junto con un botón para contactarnos por WhatsApp.
            </p>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-white/50 mb-1.5 block">Motivo</label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={5}
                placeholder="Ejemplo: Uno de los productos quedó sin stock (Maskking Extre 100K) y no llegará antes del fin de semana. Te podemos ofrecer un cambio por el modelo similar Vozol Gear 20K o un reembolso total, lo que prefieras."
                className="bg-white/5 border-white/10 text-white resize-none focus-visible:ring-red-500/40"
                data-testid="cancel-reason-textarea"
                maxLength={1000}
                autoFocus
              />
              <p className="text-right text-[10px] text-white/40 mt-1">{cancelReason.length}/1000</p>
            </div>
            <div className="rounded-lg bg-emerald-500/[0.06] border border-emerald-500/20 p-3 text-xs text-emerald-200/90 leading-relaxed">
              El cliente recibirá un correo con el motivo tal cual lo escribas + un botón <strong>"Escríbenos por WhatsApp"</strong> con este mensaje precargado para que solo te reenvíe el motivo.
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
              <Button
                variant="outline"
                onClick={() => { setCancelDialog(null); setCancelReason(''); }}
                className="h-9 rounded-full border-white/15 text-white/80 hover:bg-white/5"
                data-testid="cancel-order-cancel"
                disabled={cancelBusy}
              >
                Volver
              </Button>
              <Button
                onClick={confirmCancel}
                disabled={cancelBusy || cancelReason.trim().length < 5}
                className="h-9 rounded-full bg-red-500 hover:bg-red-600 text-white font-bold"
                data-testid="cancel-order-confirm"
              >
                {cancelBusy ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Cancelando...</> : <><XCircle className="w-3.5 h-3.5 mr-1" /> Cancelar y avisar al cliente</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PAYMENT_META = {
  approved: { label: 'Aprobado', color: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' },
  rejected: { label: 'Rechazado', color: 'bg-red-500/10 border-red-500/30 text-red-300' },
  pending_review: { label: 'Revisar', color: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-300' },
  pending: { label: 'Sin comprobante', color: 'bg-white/5 border-white/15 text-white/60' },
  cash_on_delivery: { label: 'Al entregar', color: 'bg-white/5 border-white/15 text-white/60' },
};

const PaymentBadge = ({ status }) => {
  const meta = PAYMENT_META[status] || PAYMENT_META.pending;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full ${meta.color} border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider`}>
      {meta.label}
    </span>
  );
};

const PaymentCell = ({ order, onViewProof }) => {
  const isTransfer = order.payment_method === 'transferencia';
  if (!isTransfer) {
    return (
      <span className="inline-flex items-center gap-1.5 text-white/70 text-xs">
        <Banknote className="w-3.5 h-3.5 text-emerald-400" />
        <span className="font-semibold">Efectivo</span>
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <PaymentBadge status={order.payment_status || 'pending'} />
      {order.payment_proof ? (
        <button
          onClick={onViewProof}
          className="inline-flex items-center gap-1 rounded-full bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-300 px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition"
          data-testid={`payment-view-proof-${order.order_id}`}
        >
          <Eye className="w-3 h-3" /> Ver
        </button>
      ) : (
        <span className="text-white/30 text-[10px] uppercase tracking-wider font-bold">Sin foto</span>
      )}
    </div>
  );
};

export default OrdersSection;
